import { createContext, useContext, useEffect, useState, useCallback } from "react";

type Theme = "dark" | "light";

/** Returns the theme that should be active based on current local time.
 *  Day:   06:00 – 17:59 → light
 *  Night: 18:00 – 05:59 → dark
 */
function getTimeBasedTheme(): Theme {
  const hour = new Date().getHours();
  return hour >= 6 && hour < 18 ? "light" : "dark";
}

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  /** Whether the user has manually overridden the auto theme */
  isManual: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "dark",
  toggleTheme: () => {},
  isManual: false,
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem("openim-theme");
    if (stored === "light" || stored === "dark") return stored;
    // No stored preference → use time-based default
    return getTimeBasedTheme();
  });

  // Track whether the user has manually toggled (so auto-switch won't override)
  const [isManual, setIsManual] = useState<boolean>(() => {
    return localStorage.getItem("openim-theme") !== null;
  });

  // Apply theme class to <html>
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
      root.classList.remove("light");
    } else {
      root.classList.remove("dark");
      root.classList.add("light");
    }
    localStorage.setItem("openim-theme", theme);
  }, [theme]);

  // Auto-switch based on time every minute, unless user has manually set a preference
  useEffect(() => {
    if (isManual) return;

    const tick = () => {
      const autoTheme = getTimeBasedTheme();
      setTheme((prev) => (prev !== autoTheme ? autoTheme : prev));
    };

    tick(); // run immediately
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, [isManual]);

  const toggleTheme = useCallback(() => {
    setIsManual(true);
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isManual }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}